import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import VersionChangeCard from './VersionChangeCard';
import PopOver from '../../components/deprecated/PopOver';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import { AnnotationPreviewContainer } from '../annotation/Annotation';
import AnnotationIcon from '../annotation/AnnotationIcon';
import AnnotationLink from '../annotation/AnnotationLink';
import AnnotationPreview from '../annotation/AnnotationPreview';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import { useMagneticWallLayout } from '../MagneticWallLayout';
import isContentEmpty from '../utils/isContentEmpty';

const ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationVersionChangeCard_annotation on Annotation {
    id    
    ...AnnotationPreview_annotation
  }
`);

export type AnnotationVersionChange = {
  id: string;
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  type: 'annotation';
};

interface AnnotationVersionChangeCardProps {
  versionChange: AnnotationVersionChange;
  focused: boolean;
  onHighlight: () => void;
  onRemoveHighlight: () => void;
  onFocus: () => void;
  onCopyLink: () => void;
  showAnnotationNewBadge: boolean;
  magneticWallItemSize: MagneticWallItemSize;
}

const AnnotationVersionChangeCard = ({
  versionChange: { annotation },
  focused,
  onHighlight,
  onRemoveHighlight,
  onFocus,
  onCopyLink,
  showAnnotationNewBadge,
  magneticWallItemSize,
}: AnnotationVersionChangeCardProps) => {
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  const { updateThreadHeights, layout, handlePopOverRender } =
    useMagneticWallLayout();

  useEffect(() => {
    updateThreadHeights();
  }, [focused, updateThreadHeights]);

  const trimmedLinkText = annotation.linkText?.trim() ?? '';

  const sanitizedLink = sanitizeAnnotationLink(annotation.link ?? '');
  const shouldShowLink = !isContentEmpty(annotation.content) && sanitizedLink;

  const handleClick = () => {
    onFocus();

    if (magneticWallItemSize === 'compact') {
      setIsPopOverOpen(true);
    }
  };

  const avatarWithNewBadge = (
    <VersionChangeCard.NewBadgeContainer showBadge={showAnnotationNewBadge}>
      <AnnotationIcon />
    </VersionChangeCard.NewBadgeContainer>
  );

  const fullAnnotation = (
    <>
      <AnnotationPreviewContainer onClick={handleClick}>
        <AnnotationPreview
          annotation={annotation}
          avatar={
            focused ? (
              <VersionChangeCard.CopyLinkButton onCopyLink={onCopyLink}>
                {avatarWithNewBadge}
              </VersionChangeCard.CopyLinkButton>
            ) : (
              avatarWithNewBadge
            )
          }
        />
      </AnnotationPreviewContainer>

      <AnimatePresence>
        {focused && shouldShowLink && (
          <OpacityMotionTransition key="link" transition={{ duration: 0.25 }}>
            <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
          </OpacityMotionTransition>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <MagneticWallItem.Root
      id={annotation.id}
      focused={focused}
      size={magneticWallItemSize}
      onMouseEnter={() => {
        onHighlight();
      }}
      onMouseLeave={() => {
        onRemoveHighlight();
      }}
      compactView={
        <MagneticWallItem.ResponsivePopover
          onClose={() => setIsPopOverOpen(false)}
          target={
            <VersionChangeCard.Root onClick={handleClick} focused={focused}>
              <VersionChangeCard.NewBadgeContainer
                showBadge={showAnnotationNewBadge}
              >
                <VersionChangeCard.CompactAvatarContainer>
                  <AnnotationIcon hoverable />
                </VersionChangeCard.CompactAvatarContainer>
              </VersionChangeCard.NewBadgeContainer>
            </VersionChangeCard.Root>
          }
          onPopOverHeightChange={handlePopOverRender.bind(null, annotation.id)}
          content={
            <PopOver.Container noPadding fullHeight>
              {fullAnnotation}
            </PopOver.Container>
          }
          compactPopOverSide={layout.compactPopOverSide}
          open={isPopOverOpen}
        />
      }
      fullView={
        <MagneticWallItem.Card focused={focused} focusable overlapped={false}>
          {fullAnnotation}
        </MagneticWallItem.Card>
      }
    />
  );
};

export default AnnotationVersionChangeCard;
